import { useEffect, useMemo, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { money, formatDateTime, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, HandCoins, Check, X, Send, Download, Search } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import SaldoProgressBar from "@/components/SaldoProgressBar";
import { useAuth } from "@/context/AuthContext";

export default function Adelantos() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", amount: 1000 });
  const [empSearch, setEmpSearch] = useState("");
  const [simulation, setSimulation] = useState(null);
  const [transferAdv, setTransferAdv] = useState(null);
  const [speiRef, setSpeiRef] = useState("");
  const [companies, setCompanies] = useState([]);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportCompany, setExportCompany] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter && statusFilter !== "all" ? { status: statusFilter } : {};
      const { data } = await api.get("/advances", { params });
      setList(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/employees");
        setEmployees(data.filter((e) => e.active));
      } catch (e) { /* noop */ }
    })();
    if (isAdmin) {
      (async () => {
        try {
          const { data } = await api.get("/companies");
          setCompanies(data);
        } catch (e) { /* noop */ }
      })();
    }
  }, [isAdmin]);

  const openCreate = () => {
    setForm({ employee_id: employees[0]?.id || "", amount: 1000 });
    setSimulation(null);
    setOpen(true);
  };

  // Auto-simulate when employee/amount changes
  useEffect(() => {
    if (!open || !form.employee_id || !form.amount) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/advances/simulate", null, {
          params: { employee_id: form.employee_id, amount: Number(form.amount) },
        });
        setSimulation(data);
      } catch (e) { setSimulation(null); }
    }, 250);
    return () => clearTimeout(t);
  }, [form.employee_id, form.amount, open]);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/advances", {
        employee_id: form.employee_id,
        amount: Number(form.amount),
      });
      toast.success("Solicitud creada");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const approve = async (id) => {
    try { await api.post(`/advances/${id}/approve`); toast.success("Adelanto aprobado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const reject = async (id) => {
    try { await api.post(`/advances/${id}/reject`); toast.success("Adelanto rechazado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const confirmTransfer = async () => {
    if (!speiRef.trim()) return toast.error("Referencia SPEI requerida");
    try {
      await api.post(`/advances/${transferAdv.id}/transfer`, { spei_reference: speiRef });
      toast.success("Transferencia registrada");
      setTransferAdv(null);
      setSpeiRef("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadXlsx = async () => {
    try {
      const token = localStorage.getItem("nomiflex_token");
      const qs = exportCompany && exportCompany !== "all" ? `?company_id=${encodeURIComponent(exportCompany)}` : "";
      const res = await fetch(`${API}/export/advances.xlsx${qs}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Error al descargar");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const co = companies.find((c) => c.id === exportCompany);
      a.href = url;
      a.download = co ? `adelantos_${co.rfc}.xlsx` : "adelantos.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      setExportOpen(false);
      toast.success("Excel descargado");
    } catch (e) {
      toast.error("No se pudo exportar");
    }
  };

  return (
    <div className="space-y-5" data-testid="adelantos-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#0F172A]">Adelantos</h1>
          <p className="text-sm text-[#64748B] mt-1">Solicitudes, aprobación y transferencias SPEI.</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" data-testid="export-xlsx" onClick={() => { setExportCompany("all"); setExportOpen(true); }}>
              <Download className="w-4 h-4 mr-1.5" />Excel
            </Button>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-advance-btn" onClick={openCreate} className="bg-[#1D9E75] hover:bg-[#147D5C]">
                <Plus className="w-4 h-4 mr-1.5" />Nueva solicitud
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Nueva solicitud de adelanto</DialogTitle></DialogHeader>
              <form onSubmit={onSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-[#475569]">Empleado</Label>
                  <Select
                    value={form.employee_id}
                    onValueChange={(v) => setForm({ ...form, employee_id: v })}
                    onOpenChange={(o) => { if (!o) setEmpSearch(""); }}
                  >
                    <SelectTrigger data-testid="adv-emp-select"><SelectValue placeholder="Seleccionar empleado" /></SelectTrigger>
                    <SelectContent>
                      <div className="sticky top-0 z-10 bg-white border-b border-[#E2E8F0] p-2">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#94A3B8] pointer-events-none" />
                          <Input
                            data-testid="adv-emp-search"
                            type="text"
                            value={empSearch}
                            onChange={(e) => setEmpSearch(e.target.value)}
                            placeholder="Buscar empleado..."
                            // Prevent Radix Select from intercepting key events (which would
                            // re-focus its internal listbox and steal characters).
                            onKeyDown={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                            className="h-8 pl-8 text-sm"
                            autoFocus
                          />
                        </div>
                      </div>
                      {(() => {
                        const q = empSearch.trim().toLowerCase();
                        const filtered = q
                          ? employees.filter((e) => e.full_name?.toLowerCase().includes(q))
                          : employees;
                        if (filtered.length === 0) {
                          return (
                            <div data-testid="adv-emp-empty" className="px-3 py-6 text-center text-xs text-[#64748B]">
                              Sin coincidencias
                            </div>
                          );
                        }
                        return filtered.map((e) => (
                          <SelectItem key={e.id} value={e.id}>
                            {e.full_name} — {money(e.saldo?.available)} disp.
                          </SelectItem>
                        ));
                      })()}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-[#475569]">Monto solicitado</Label>
                  <Input
                    data-testid="adv-amount-input"
                    type="number" min="1" step="1"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    required
                  />
                </div>

                {(() => {
                  const selected = employees.find((e) => e.id === form.employee_id);
                  if (selected && !selected.authorization_signed) {
                    return (
                      <div
                        data-testid="auth-missing-banner"
                        className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                      >
                        <strong>Falta la autorización firmada del empleado.</strong>{" "}
                        No se puede capturar un adelanto hasta que el admin la marque como firmada en el perfil del empleado.
                      </div>
                    );
                  }
                  return null;
                })()}

                {simulation && (
                  <div className={`rounded-md border p-4 text-sm ${simulation.valid ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
                    <SaldoProgressBar saldo={simulation.saldo} />
                    <div className="mt-3 pt-3 border-t border-[#E2E8F0] grid grid-cols-2 gap-x-4 gap-y-1.5 tabular-nums">
                      <div className="text-[#64748B]">Monto solicitado:</div><div className="font-semibold text-right" data-testid="sim-amount">{money(simulation.amount)}</div>
                      <div className="text-[#64748B]">Comisión NomiDiem:</div><div className="font-semibold text-right" data-testid="sim-fee">{money(simulation.fee)}</div>
                      <div className="text-[#64748B]">IVA (16%):</div><div className="font-semibold text-right" data-testid="sim-iva">{money(simulation.iva)}</div>
                      <div className="text-[#B45309]">Total descontado al empleado:</div><div className="font-semibold text-right text-[#B45309]" data-testid="sim-total-discount">{money(simulation.total_discounted)}</div>
                      <div className="text-[#147D5C] font-semibold">
                        {isAdmin ? "Transferido al empleado:" : "Monto que recibirá el empleado:"}
                      </div>
                      <div className="font-semibold text-right text-[#147D5C]" data-testid="sim-net">{money(simulation.net_amount)}</div>
                      <div className="text-[#64748B]">
                        {isAdmin ? "A recuperar de la empresa:" : "A descontar en nómina y transferir a NomiDiem:"}
                      </div>
                      <div className="font-semibold text-right" data-testid="sim-gross">{money(simulation.total_to_recover)}</div>
                      <div className="text-[#64748B] pt-2 border-t border-[#E2E8F0] col-span-2" />
                      <div className="text-[#64748B]">Saldo devengado:</div><div className="font-semibold text-right" data-testid="sim-gross-accrued">{money(simulation.saldo?.gross_accrued)}</div>
                      <div className="text-[#64748B]">Adelantos activos del periodo:</div><div className="font-semibold text-right text-[#B45309]" data-testid="sim-outstanding">{money(simulation.saldo?.outstanding_advances)}</div>
                      <div className="text-[#64748B]">Máx. 30% del periodo:</div><div className="font-semibold text-right">{money(simulation.saldo?.max_allowed_30pct)}</div>
                      <div className="text-[#147D5C] font-semibold">Saldo disponible para solicitar:</div><div className="font-semibold text-right text-[#147D5C]" data-testid="sim-available">{money(simulation.saldo?.available)}</div>
                    </div>
                    <p className="mt-3 pt-3 border-t border-[#E2E8F0] text-[11px] text-[#64748B] leading-snug" data-testid="saldo-net-salary-note">
                      El saldo devengado se calcula sobre el <strong>salario mensual neto</strong> del empleado (monto que recibe en su cuenta después de todas las deducciones de ley: ISR, IMSS, INFONAVIT).
                    </p>
                    {!simulation.valid && (
                      <div className="mt-3 pt-3 border-t border-rose-300 text-xs text-rose-700 space-y-0.5">
                        {simulation.reasons.map((r, i) => <div key={i}>• {r}</div>)}
                      </div>
                    )}
                  </div>
                )}

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                  <Button
                    type="submit"
                    disabled={!simulation?.valid}
                    data-testid="adv-submit-btn"
                    className="bg-[#1D9E75] hover:bg-[#147D5C]"
                  >
                    Crear solicitud
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Label className="text-xs uppercase tracking-wider text-[#475569]">Estado</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-status" className="max-w-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="PENDIENTE">Pendientes</SelectItem>
              <SelectItem value="APROBADO">Aprobados</SelectItem>
              <SelectItem value="TRANSFERIDO">Transferidos</SelectItem>
              <SelectItem value="LIQUIDADO">Liquidados</SelectItem>
              <SelectItem value="RECHAZADO">Rechazados</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="overflow-hidden p-0">
        {loading ? (
          <div className="p-8 flex justify-center"><div className="nomi-spin" /></div>
        ) : list.length === 0 ? (
          <div className="p-12 text-center">
            <HandCoins className="w-10 h-10 text-[#CBD5E1] mx-auto" />
            <div className="mt-3 text-sm text-[#64748B]">No hay adelantos</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="adelantos-table">
              <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#64748B]">
                  <th className="px-4 py-3 font-medium">Fecha</th>
                  <th className="px-4 py-3 font-medium">Empleado</th>
                  {isAdmin && <th className="px-4 py-3 font-medium">Empresa</th>}
                  <th className="px-4 py-3 font-medium text-right">
                    {isAdmin ? "Monto solicitado" : "Monto que recibió el empleado"}
                  </th>
                  <th className="px-4 py-3 font-medium text-right">Comisión</th>
                  <th className="px-4 py-3 font-medium text-right">IVA (16%)</th>
                  <th className="px-4 py-3 font-medium text-right">
                    {isAdmin ? "Transferido al empleado" : "A descontar y transferir a NomiDiem"}
                  </th>
                  {isAdmin && <th className="px-4 py-3 font-medium text-right">A recuperar de la empresa</th>}
                  <th className="px-4 py-3 font-medium">SPEI</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {list.map((a) => {
                  const fee = Number(a.fee || 0);
                  const iva = a.iva != null ? Number(a.iva) : Math.round(fee * 0.16 * 100) / 100;
                  const gross = Number(a.total_to_recover || a.amount || 0);
                  const net = a.net_amount != null
                    ? Number(a.net_amount)
                    : Number(a.amount || 0) - fee - iva;
                  return (
                  <tr key={a.id} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC]">
                    <td className="px-4 py-3 text-xs text-[#64748B]">{formatDateTime(a.requested_at)}</td>
                    <td className="px-4 py-3 font-medium text-[#0F172A]">{a.employee_name}</td>
                    {isAdmin && <td className="px-4 py-3 text-[#475569]">{a.company_name}</td>}
                    <td className="px-4 py-3 text-right tabular-nums">
                      {isAdmin ? money(a.amount) : money(net)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-[#64748B]">{money(fee)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-[#64748B]">{money(iva)}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-[#147D5C]">
                      {isAdmin ? money(net) : money(gross)}
                    </td>
                    {isAdmin && <td className="px-4 py-3 text-right tabular-nums font-semibold">{money(gross)}</td>}
                    <td className="px-4 py-3 font-mono text-xs">{a.spei_reference || "—"}</td>
                    <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        {a.status === "PENDIENTE" && isAdmin && (
                          <>
                            <Button size="sm" data-testid={`approve-${a.id}`} onClick={() => approve(a.id)} className="bg-[#1D9E75] hover:bg-[#147D5C] h-7">
                              <Check className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="outline" data-testid={`reject-${a.id}`} onClick={() => reject(a.id)} className="h-7">
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                        {a.status === "PENDIENTE" && !isAdmin && (
                          <span className="text-xs text-[#94A3B8] italic">En revisión</span>
                        )}
                        {a.status === "APROBADO" && isAdmin && (
                          <Button size="sm" data-testid={`transfer-${a.id}`} onClick={() => setTransferAdv(a)} className="bg-indigo-600 hover:bg-indigo-700 h-7">
                            <Send className="w-3.5 h-3.5 mr-1" />SPEI
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Export dialog: pick company before downloading */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle>Exportar adelantos a Excel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#64748B]">
              Selecciona una empresa para filtrar los adelantos, o elige "Todas las empresas" para exportar todo.
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-[#475569]">Empresa</Label>
              <Select value={exportCompany} onValueChange={setExportCompany}>
                <SelectTrigger data-testid="export-company-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las empresas</SelectItem>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setExportOpen(false)}>Cancelar</Button>
              <Button data-testid="export-confirm-btn" onClick={downloadXlsx} className="bg-[#1D9E75] hover:bg-[#147D5C]">
                <Download className="w-4 h-4 mr-1.5" />Descargar Excel
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer dialog */}
      <Dialog open={!!transferAdv} onOpenChange={(v) => !v && setTransferAdv(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Registrar transferencia SPEI</DialogTitle></DialogHeader>
          {transferAdv && (
            <div className="space-y-4">
              <div className="text-sm bg-[#F8FAFC] border border-[#E2E8F0] rounded-md p-3 space-y-1">
                <div className="flex justify-between"><span className="text-[#64748B]">Empleado:</span><span className="font-medium">{transferAdv.employee_name}</span></div>
                <div className="flex justify-between"><span className="text-[#64748B]">Monto:</span><span className="font-semibold tabular-nums">{money(transferAdv.amount)}</span></div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-[#475569]">Referencia SPEI / CLAVE DE RASTREO</Label>
                <Input
                  data-testid="spei-ref-input"
                  value={speiRef}
                  onChange={(e) => setSpeiRef(e.target.value)}
                  placeholder="Ej: BANORTE2025021600001"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setTransferAdv(null)}>Cancelar</Button>
                <Button onClick={confirmTransfer} data-testid="confirm-spei-btn" className="bg-[#1D9E75] hover:bg-[#147D5C]">Confirmar transferencia</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

